import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { placeCreatedRowFirst } from "utils/helpers";

import {
  UnderCarriageForm,
  UnderCarriageListMeta,
  UnderCarriageRow,
  UnderCarriageUpdate,
} from "../types";
import { initialUnderCarriageForm, initialState } from "./initialState";

export const sliceKey = "underCarriageState";

type UnderCarriageListSuccessPayload = {
  data: UnderCarriageRow[];
  meta: UnderCarriageListMeta;
};

const underCarriageSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getUnderCarriageList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getUnderCarriageListSuccess(
      state,
      action: PayloadAction<UnderCarriageListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getUnderCarriageListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<UnderCarriageListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialUnderCarriageForm;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof UnderCarriageForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialUnderCarriageForm;
    },

    addUnderCarriage(state) {
      state.saving = true;
      state.error = null;
    },

    addUnderCarriageSuccess(state, action: PayloadAction<UnderCarriageRow>) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialUnderCarriageForm;
      state.error = null;
      state.list = placeCreatedRowFirst(state.list, action.payload);
    },

    addUnderCarriageError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    updateUnderCarriage(state, _action: PayloadAction<UnderCarriageUpdate>) {
      state.saving = true;
      state.error = null;
    },

    updateUnderCarriageSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateUnderCarriageError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    deleteUnderCarriage(state, _action: PayloadAction<string>) {
      state.saving = true;
      state.error = null;
    },

    deleteUnderCarriageSuccess(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = null;
      state.list = state.list.filter(
        (item) => item.subChassisId !== action.payload
      );
      if (state.detail?.subChassisId === action.payload) {
        state.detail = null;
        state.detailLoading = false;
        state.detailError = null;
      }
    },

    deleteUnderCarriageError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    getUnderCarriageDetail(state, _action: PayloadAction<string>) {
      state.detailLoading = true;
      state.detailError = null;
      state.detail = null;
    },

    getUnderCarriageDetailSuccess(state, action: PayloadAction<UnderCarriageRow>) {
      state.detailLoading = false;
      state.detail = action.payload;
      state.detailError = null;
    },

    getUnderCarriageDetailError(state, action: PayloadAction<string>) {
      state.detailLoading = false;
      state.detail = null;
      state.detailError = action.payload;
    },
  },
});

export const { actions: underCarriageActions, reducer } = underCarriageSlice;
